import React from 'react';
import {
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Box,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import {
  MODEL_CONFIG_CONSTRAINTS,
  MODEL_CONFIG_HELPERS,
} from '../../../config/constants';

/**
 * ModelConfiguration Component
 *
 * Component for configuring LLM model parameters including provider,
 * model selection, and various model configuration options.
 *
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data object containing model config
 * @param {Array} props.providers - Available providers
 * @param {Object} props.providerModels - Models available for each provider
 * @param {boolean} props.loadingProviders - Whether providers are loading
 * @param {boolean} props.showModelConfig - Whether to show model configuration
 * @param {Function} props.handleModelConfigChange - Handler for model config changes
 * @param {Function} props.getModelsForProvider - Function to get models for a provider
 * @param {Function} props.getDefaultModelForProvider - Function to get default model for provider
 * @param {Function} props.setShowModelConfig - Setter for showing model config
 */
const ModelConfiguration = ({
  formData,
  providers,
  providerModels,
  loadingProviders,
  showModelConfig,
  handleModelConfigChange,
  getModelsForProvider,
  getDefaultModelForProvider,
  setShowModelConfig,
}) => {
  // Get current provider (no fallback - use what's in formData)
  const currentProvider = formData.model_config.provider || '';

  // Get models for current provider
  const availableModels = getModelsForProvider(currentProvider);

  // Get current model (no fallback - use what's in formData)
  const currentModel = formData.model_config.model_name || '';

  // Determine if Selects should be enabled
  // Provider Select: enabled when providers are available and not loading
  const isProviderSelectEnabled = !loadingProviders && providers.length > 0;

  // Model Select: enabled when provider is selected and models are available for that provider
  // OR when providers exist but current provider has no models (user can switch provider first)
  const isModelSelectEnabled =
    !loadingProviders &&
    currentProvider &&
    (availableModels.length > 0 || providers.length > 0);

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <SettingsIcon sx={{ mr: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Model Configuration
        </Typography>
        <IconButton
          size="small"
          onClick={() => setShowModelConfig(!showModelConfig)}
          sx={{ ml: 'auto' }}
        >
          <PreviewIcon />
        </IconButton>
      </Box>

      {showModelConfig && (
        <Box>
          {/* Provider Selection */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Provider</InputLabel>
            <Select
              value={currentProvider || ''}
              onChange={e => {
                const provider = e.target.value;
                handleModelConfigChange('provider', provider);
                // Auto-select first model when provider changes
                const defaultModel = getDefaultModelForProvider(provider);
                if (defaultModel) {
                  // This will trigger max_tokens sync in handleModelConfigChange
                  handleModelConfigChange('model_name', defaultModel);
                } else {
                  // If no default model, clear the selection
                  handleModelConfigChange('model_name', '');
                }
              }}
              label="Provider"
              disabled={!isProviderSelectEnabled}
              displayEmpty
              renderValue={selected => {
                if (!selected) {
                  return <em>No provider selected</em>;
                }
                // Find provider in list
                const provider = providers.find(p => p.id === selected);
                if (provider) {
                  return provider.name;
                }
                // If provider not in list (e.g., from template but not in available adapters), show it anyway
                return selected;
              }}
            >
              {providers.length === 0 ? (
                <MenuItem value="" disabled>
                  No providers available
                </MenuItem>
              ) : (
                providers.map(provider => (
                  <MenuItem key={provider.id} value={provider.id}>
                    {provider.name}
                  </MenuItem>
                ))
              )}
              {/* If current provider is not in the list, add it as a disabled option to prevent MUI error */}
              {currentProvider &&
                !providers.find(p => p.id === currentProvider) && (
                  <MenuItem value={currentProvider} disabled>
                    {currentProvider} (not available)
                  </MenuItem>
                )}
            </Select>
            {providers.length === 0 && (
              <Box
                sx={{ mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}
              >
                No providers configured. Add providers from Settings first.
              </Box>
            )}
          </FormControl>

          {/* Model Selection based on Provider */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Model</InputLabel>
            <Select
              value={currentModel || ''}
              onChange={e => {
                // This will trigger max_tokens sync in handleModelConfigChange
                handleModelConfigChange('model_name', e.target.value);
              }}
              label="Model"
              disabled={!isModelSelectEnabled}
              displayEmpty
              renderValue={selected => {
                if (!selected) {
                  return <em>No model selected</em>;
                }
                // Find model in list
                const model = availableModels.find(
                  m => m.id === selected || m.value === selected
                );
                if (model) {
                  return model.label || model.name || selected;
                }
                // If model not in list (e.g., from template but not in available models), show it anyway
                return selected;
              }}
            >
              {availableModels.length === 0 ? (
                <MenuItem value="" disabled>
                  No models available
                </MenuItem>
              ) : (
                availableModels.map(model => (
                  <MenuItem
                    key={model.value || model.id}
                    value={model.value || model.id}
                  >
                    {model.label || model.name}
                  </MenuItem>
                ))
              )}
              {/* If current model is not in the list, add it as a disabled option to prevent MUI error */}
              {currentModel &&
                !availableModels.find(
                  m =>
                    m.id === currentModel ||
                    m.value === currentModel ||
                    m.name === currentModel
                ) && (
                  <MenuItem value={currentModel} disabled>
                    {currentModel} (not available)
                  </MenuItem>
                )}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Temperature"
            type="number"
            value={formData.model_config.temperature}
            onChange={e =>
              handleModelConfigChange('temperature', parseFloat(e.target.value))
            }
            inputProps={MODEL_CONFIG_CONSTRAINTS.temperature}
            helperText={MODEL_CONFIG_HELPERS.temperature}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Max Tokens"
            type="number"
            value={formData.model_config.max_tokens}
            onChange={e =>
              handleModelConfigChange('max_tokens', parseInt(e.target.value))
            }
            inputProps={MODEL_CONFIG_CONSTRAINTS.max_tokens}
            helperText={MODEL_CONFIG_HELPERS.max_tokens}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Top P"
            type="number"
            value={formData.model_config.top_p}
            onChange={e =>
              handleModelConfigChange('top_p', parseFloat(e.target.value))
            }
            inputProps={MODEL_CONFIG_CONSTRAINTS.top_p}
            helperText={MODEL_CONFIG_HELPERS.top_p}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Frequency Penalty"
            type="number"
            value={formData.model_config.frequency_penalty}
            onChange={e =>
              handleModelConfigChange(
                'frequency_penalty',
                parseFloat(e.target.value)
              )
            }
            inputProps={MODEL_CONFIG_CONSTRAINTS.frequency_penalty}
            helperText={MODEL_CONFIG_HELPERS.frequency_penalty}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Presence Penalty"
            type="number"
            value={formData.model_config.presence_penalty}
            onChange={e =>
              handleModelConfigChange(
                'presence_penalty',
                parseFloat(e.target.value)
              )
            }
            inputProps={MODEL_CONFIG_CONSTRAINTS.presence_penalty}
            helperText={MODEL_CONFIG_HELPERS.presence_penalty}
          />
        </Box>
      )}
    </Paper>
  );
};

export default ModelConfiguration;
